import classNames from 'classnames'
import { format, isValid, Locale, parse, setDefaultOptions } from 'date-fns'
import React, { useRef, useState } from 'react'
import { DayPicker, getDefaultClassNames, Matcher, MonthCaptionProps } from 'react-day-picker'
import 'react-day-picker/style.css'
import ReactDOM from 'react-dom'
import { usePopper } from 'react-popper'
import { Transition } from 'react-transition-group'

import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '../../icon/icon.js'
import { Textfield, TextfieldProps } from '../textfield/textfield.js'

export interface DatepickerProps extends Omit<TextfieldProps, 'value' | 'onChange'> {
    nextMonthText: string
    prevMonthText: string
    className?: string
    dateFormat?: string
    disabledDays?: DatepickerDisabledDays
    locale?: Locale
    initialValue: Date | undefined
    onChange?: (date: Date | undefined, text: string) => void
}

export type DatepickerDisabledDays = Matcher | Matcher[]

export interface DatepickerCaptionProps extends MonthCaptionProps {
    nextMonthText: DatepickerProps['nextMonthText']
    prevMonthText: DatepickerProps['prevMonthText']
}

export const Datepicker = (props: DatepickerProps): JSX.Element => {
    const popper = useRef<HTMLElement | null>(null)

    const { dateFormat = 'dd.MM.yyyy', disabledDays, locale, nextMonthText, prevMonthText, ...textfieldProps } = props

    const [isShown, setIsShown] = useState<boolean>(false)
    const [selected, setSelected] = useState<Date | undefined>(props.initialValue)
    const [inputValue, setInputValue] = useState<string>(
        props.initialValue ? format(props.initialValue, dateFormat) : '',
    )
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
    const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)

    const { attributes, styles } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
    })

    setDefaultOptions({
        locale,
        weekStartsOn: 1,
    })

    const className: string = classNames('datepicker', isShown && 'is-focused', props.className)

    const BEM = (state?: string): string => {
        const classArray = ['datepicker__popper']

        if (state) {
            classArray.push(`is-${state}`)
        }

        if (isShown) {
            classArray.push('is-shown')
        }

        return classArray.join(' ')
    }

    const setRef = (el: HTMLElement | null): void => {
        if (el && popper.current !== el) {
            popper.current = el
        }

        setPopperElement(el)
    }

    const showPopper = (): void => {
        setIsShown(true)
    }

    const hidePopper = (): void => {
        setIsShown(false)
    }

    const hideIfOutside = (target: Element | null): void => {
        if (isShown) {
            while (target !== null && isShown) {
                if (target === popper.current || target === referenceElement) {
                    return
                }

                target = target.parentElement
            }

            hidePopper()
        }
    }

    const onClickOutside = (event: MouseEvent): void => {
        if (event.target instanceof Element) {
            hideIfOutside(event.target)
        }
    }

    const onEntered = (): void => {
        document.addEventListener('click', onClickOutside)
    }

    const onExited = (): void => {
        document.removeEventListener('click', onClickOutside)
    }

    const onChange = (date: Date | undefined, text: string) => {
        setSelected(date)

        if (props.onChange) {
            props.onChange(date, text)
        }
    }

    const handleInputChange = (inputVal: string) => {
        setInputValue(inputVal)
        const date = parse(inputVal, dateFormat, new Date())

        if (isValid(date)) {
            onChange(date, inputVal)
        } else {
            onChange(undefined, '')
        }
    }

    const handleDaySelect = (date: Date | undefined) => {
        const text = date ? format(date, dateFormat) : ''
        setInputValue(text)
        onChange(date, text)

        if (date) {
            hidePopper()
        }
    }

    const renderPopper = (): JSX.Element => {
        const defClasses = getDefaultClassNames()

        const content = (state?: string) => (
            <div
                {...attributes.popper}
                className={BEM(state)}
                style={styles.popper}
                ref={setRef}
                tabIndex={-1}
                role="dialog"
            >
                <DayPicker
                    autoFocus={isShown}
                    className="datepicker__content"
                    classNames={{
                        day: classNames(defClasses.day, 'datepicker__day'),
                        day_button: classNames(defClasses.day_button, 'datepicker__day-button'),
                        disabled: classNames(defClasses.disabled, 'datepicker__day--disabled'),
                        outside: classNames(defClasses.outside, 'datepicker__day--outside'),
                        selected: classNames(defClasses.selected, 'datepicker__day--selected'),
                        today: classNames('datepicker__day--today'),
                        weekday: classNames(defClasses.weekday, 'datepicker__weekday'),
                    }}
                    components={{
                        Chevron({ orientation }) {
                            return orientation === 'left' ? (
                                <ChevronLeftIcon className="datepicker__chevron" />
                            ) : (
                                <ChevronRightIcon className="datepicker__chevron" />
                            )
                        },
                    }}
                    defaultMonth={selected}
                    disabled={disabledDays}
                    locale={locale}
                    mode="single"
                    navLayout="around"
                    onSelect={handleDaySelect}
                    selected={selected}
                    showOutsideDays={true}
                />
            </div>
        )

        return (
            <Transition
                in={isShown}
                timeout={{ enter: 0, exit: 100 }}
                duration={150}
                mountOnEnter={true}
                unmountOnExit={true}
                onEntered={onEntered}
                onExited={onExited}
            >
                {content}
            </Transition>
        )
    }

    return (
        <>
            <Textfield
                {...textfieldProps}
                attributes={{
                    autoComplete: 'off',
                }}
                className={className}
                elementRef={setReferenceElement}
                icon={CalendarIcon}
                onChange={handleInputChange}
                onFocus={showPopper}
                value={inputValue}
            />
            {ReactDOM.createPortal(renderPopper(), document.body)}
        </>
    )
}
